import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { organizationsApi } from '../services/api';
import { getHubRoute } from '../utils/hubRoutes';
import { createTopLevelOrganization } from '../utils/organizations';

const AuthContext = createContext({
    currentUser: null,
    userClaims: null,
    organization: null,
    organizationType: null,
    isOrgOwner: false,
    login: async () => null,
    signup: async () => null,
    logout: async () => { },
    selectOrganization: async () => null,
    refreshClaims: async () => { },
    loading: true
});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userClaims, setUserClaims] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const claimsFetchGeneration = useRef(0);

    const fetchUserClaims = useCallback(async (user) => {
        const generation = ++claimsFetchGeneration.current;
        const isCurrent = () => generation === claimsFetchGeneration.current;

        if (!user) {
            if (isCurrent()) {
                setUserClaims(null);
                setOrganization(null);
            }
            return null;
        }

        try {
            const tokenResult = await user.getIdTokenResult();
            const claims = tokenResult.claims;
            const claimsData = {
                orgId: claims.org || null,
            };

            if (!isCurrent()) {
                return null;
            }
            setUserClaims(claimsData);

            if (claimsData.orgId) {
                try {
                    const orgData = await organizationsApi.getById(claimsData.orgId);
                    if (isCurrent()) {
                        setOrganization(orgData);
                    }
                    return orgData;
                } catch (error) {
                    console.error('Error fetching organization:', error);
                    if (isCurrent()) {
                        setOrganization(null);
                    }
                    return null;
                }
            }

            if (isCurrent()) {
                setOrganization(null);
            }
            return null;
        } catch (error) {
            console.error('Error fetching user claims:', error);
            if (isCurrent()) {
                setUserClaims(null);
                setOrganization(null);
            }
            return null;
        }
    }, []);

    const refreshClaims = useCallback(async () => {
        if (!currentUser) {
            return null;
        }
        await currentUser.getIdToken(true);
        return fetchUserClaims(currentUser);
    }, [currentUser, fetchUserClaims]);

    const selectOrganization = useCallback(async (orgId) => {
        const orgData = await organizationsApi.select(orgId);
        if (currentUser) {
            await currentUser.getIdToken(true);
            await fetchUserClaims(currentUser);
        }
        return orgData;
    }, [currentUser, fetchUserClaims]);

    const login = async (email, password) => {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const orgData = await fetchUserClaims(userCredential.user);
        if (!orgData) {
            return '/hubs';
        }
        return getHubRoute(orgData);
    };

    const signup = async ({ email, password, name, organizationType }) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        try {
            const org = await createTopLevelOrganization({ name, organizationType });
            const selected = await organizationsApi.select(org.id);
            await user.getIdToken(true);
            const orgData = await fetchUserClaims(user);
            if (!orgData && selected) {
                setUserClaims({ orgId: selected.id });
                setOrganization(selected);
            }
            return getHubRoute(selected || org);
        } catch (error) {
            console.error('Signup organization setup failed:', error);
            await fetchUserClaims(user);
            return '/hubs';
        }
    };

    const logout = async () => {
        await signOut(auth);
        setUserClaims(null);
        setOrganization(null);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            await fetchUserClaims(user);
            setLoading(false);
        });

        return unsubscribe;
    }, [fetchUserClaims]);

    const isOrgOwner = useMemo(() => {
        if (!currentUser?.uid || !organization?.created_by) {
            return false;
        }
        return organization.created_by === currentUser.uid;
    }, [currentUser?.uid, organization?.created_by]);

    const organizationType = organization?.organization_type || null;

    const value = {
        currentUser,
        userClaims,
        organization,
        organizationType,
        isOrgOwner,
        login,
        signup,
        logout,
        selectOrganization,
        refreshClaims,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
